import { Info } from 'lucide-react';
import type { Release, StreamLinks } from '@/types';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CreditsList, hasCredits } from '@/components/list/CreditsList';
import {
  hasStreamLinks,
  StreamLinksList,
} from '@/components/list/StreamLinks';

export { hasStreamLinks };

export function hasReleaseInfo(
  credits?: Release['credits'],
  streamLinks?: StreamLinks | null | undefined,
  /** When false, ignore stream links (desktop popover = credits only). */
  includeListen = true,
  /** Include “(Track N)” / track.credits for this position. */
  trackPosition?: number | null,
  trackCredits?: { role: string; name: string }[] | null,
): boolean {
  return (
    hasCredits(credits, trackPosition, trackCredits) ||
    (includeListen && hasStreamLinks(streamLinks))
  );
}

export function ReleaseInfoBody({
  credits,
  streamLinks,
  showListen = false,
  trackPosition,
  trackCredits,
}: {
  credits?: Release['credits'];
  streamLinks?: StreamLinks | null | undefined;
  /** Mobile only — streaming / Listen section. */
  showListen?: boolean;
  trackPosition?: number | null;
  trackCredits?: { role: string; name: string }[] | null;
}) {
  const listen = showListen && hasStreamLinks(streamLinks);
  const creditsOk = hasCredits(credits, trackPosition, trackCredits);

  return (
    <>
      {creditsOk ? (
        <div className="release-info-section">
          <p className="release-credits-popover-title">Credits</p>
          <CreditsList
            credits={credits}
            trackPosition={trackPosition}
            trackCredits={trackCredits}
          />
        </div>
      ) : null}
      {listen ? (
        <div className="release-info-section release-info-section--listen">
          <p className="release-credits-popover-title">Listen</p>
          <StreamLinksList streamLinks={streamLinks} variant="popover" />
        </div>
      ) : null}
    </>
  );
}

type ReleaseInfoButtonProps = {
  credits?: Release['credits'];
  streamLinks?: StreamLinks | null | undefined;
  className?: string;
  /** Override default side; mobile album chrome prefers top */
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /**
   * Current track (1-based). Matching “(Track N)” release rows and
   * `trackCredits` are included when set.
   */
  trackPosition?: number | null;
  /** Credits from the current track object (writers, etc.). */
  trackCredits?: { role: string; name: string }[] | null;
  'aria-label'?: string;
};

/** Info control: Listen + Credits on mobile; Credits only on desktop. */
export function ReleaseInfoButton({
  credits,
  streamLinks,
  className = 'album-action-info',
  side,
  align = 'end',
  trackPosition,
  trackCredits,
  'aria-label': ariaLabel = 'Release info',
}: ReleaseInfoButtonProps) {
  const isMobile = useIsMobile();
  const scopedTrack = trackPosition;
  if (
    !hasReleaseInfo(credits, streamLinks, isMobile, scopedTrack, trackCredits)
  ) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={className} aria-label={ariaLabel}>
          <Info size={18} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side={side ?? (isMobile ? 'top' : 'bottom')}
        sideOffset={10}
        collisionPadding={16}
        className={
          isMobile
            ? 'release-credits-popover rounded-xl'
            : 'release-credits-popover rounded-md'
        }
      >
        <ReleaseInfoBody
          credits={credits}
          streamLinks={streamLinks}
          showListen={isMobile}
          trackPosition={scopedTrack}
          trackCredits={trackCredits}
        />
      </PopoverContent>
    </Popover>
  );
}
